package com.havn.app.features.tasks.presentation

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.havn.app.core.designsystem.HavnCard
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.localization.HavnStrings
import com.havn.app.core.localization.LocalHavnStrings
import com.havn.app.features.tasks.domain.HavnTask
import com.havn.app.features.tasks.domain.HavnTaskPriority
import com.havn.app.features.tasks.domain.HavnTaskStatus
import com.havn.app.ui.theme.LocalHavnThemeTokens

@Composable
fun LinkedTasksSection(
    tasks: List<HavnTask>,
    onOpenTask: (String) -> Unit,
    modifier: Modifier = Modifier
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    HavnCard(modifier = modifier) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(strings.linkedTasks, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
                Surface(
                    color = theme.primaryAccent.copy(alpha = 0.14f),
                    shape = MaterialTheme.shapes.small,
                    border = BorderStroke(1.dp, theme.primaryAccent.copy(alpha = 0.45f))
                ) {
                    Text(
                        tasks.size.toString(),
                        color = theme.primaryAccent,
                        style = MaterialTheme.typography.labelLarge,
                        modifier = Modifier.padding(horizontal = 9.dp, vertical = 4.dp)
                    )
                }
            }
            if (tasks.isEmpty()) {
                Text(strings.noLinkedTasks, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
            } else {
                tasks
                    .sortedWith(compareByDescending<HavnTask> { it.updatedAt ?: it.createdAt ?: "" }.thenBy { it.dueAt ?: "9999" })
                    .take(8)
                    .forEach { task ->
                        LinkedTaskRow(task = task, onClick = { onOpenTask(task.id) })
                    }
            }
        }
    }
}

@Composable
private fun LinkedTaskRow(task: HavnTask, onClick: () -> Unit) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    Surface(
        color = theme.surface,
        shape = MaterialTheme.shapes.medium,
        border = BorderStroke(1.dp, theme.border),
        modifier = Modifier.fillMaxWidth().clickable(onClick = onClick)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth().padding(12.dp),
            horizontalArrangement = Arrangement.spacedBy(10.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            HavnIcon(HavnIconKey.Check, contentDescription = null, tint = task.statusAccent(), modifier = Modifier.size(18.dp))
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(5.dp)) {
                Text(task.title, color = theme.primaryText, style = MaterialTheme.typography.titleSmall, maxLines = 1, overflow = TextOverflow.Ellipsis)
                Text(
                    task.dueAt?.readableTaskDateTime() ?: task.updatedAt?.readableTaskDateTime() ?: task.createdAt?.readableTaskDateTime() ?: strings.noDueDate,
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.labelMedium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
            LinkedTaskChip(task.priority.localizedLabel(strings), task.priority.priorityAccent(), solid = true)
            LinkedTaskChip(task.status.localizedLabel(strings), task.statusAccent())
            HavnIcon(HavnIconKey.ArrowRight, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(18.dp))
        }
    }
}

@Composable
private fun LinkedTaskChip(label: String, accent: Color, solid: Boolean = false) {
    val background = if (solid) accent else accent.copy(alpha = 0.18f)
    val textColor = if (solid) background.taskReadableTextColor() else accent
    Surface(
        color = background,
        shape = MaterialTheme.shapes.small,
        border = BorderStroke(1.dp, accent.copy(alpha = 0.45f))
    ) {
        Text(
            label,
            color = textColor,
            style = MaterialTheme.typography.labelSmall,
            modifier = Modifier.padding(horizontal = 7.dp, vertical = 4.dp),
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
    }
}

private fun Color.taskReadableTextColor(): Color {
    val luminance = (0.299f * red) + (0.587f * green) + (0.114f * blue)
    return if (luminance > 0.58f) Color.Black else Color.White
}

@Composable
private fun HavnTask.statusAccent(): Color {
    val theme = LocalHavnThemeTokens.current
    return when (status) {
        HavnTaskStatus.Done -> theme.success
        HavnTaskStatus.Overdue -> theme.danger
        HavnTaskStatus.InProgress -> theme.warning
        HavnTaskStatus.Cancelled -> theme.mutedText
        HavnTaskStatus.Open -> theme.primaryAccent
    }
}

@Composable
private fun HavnTaskPriority.priorityAccent(): Color {
    val theme = LocalHavnThemeTokens.current
    return when (this) {
        HavnTaskPriority.Low -> theme.cold
        HavnTaskPriority.Medium -> theme.success
        HavnTaskPriority.High -> theme.warning
        HavnTaskPriority.Urgent -> theme.danger
    }
}

private fun HavnTaskPriority.localizedLabel(strings: HavnStrings): String = when (this) {
    HavnTaskPriority.Low -> strings.taskPriorityLow
    HavnTaskPriority.Medium -> strings.taskPriorityMedium
    HavnTaskPriority.High -> strings.taskPriorityHigh
    HavnTaskPriority.Urgent -> strings.taskPriorityUrgent
}

private fun HavnTaskStatus.localizedLabel(strings: HavnStrings): String = when (this) {
    HavnTaskStatus.Open -> strings.taskStatusOpen
    HavnTaskStatus.InProgress -> strings.taskStatusInProgress
    HavnTaskStatus.Done -> strings.taskStatusDone
    HavnTaskStatus.Overdue -> strings.taskStatusOverdue
    HavnTaskStatus.Cancelled -> strings.taskStatusCancelled
}

private fun String.readableTaskDateTime(): String {
    val clean = replace("T", " ").substringBefore(".").substringBefore("+").substringBefore("Z")
    val date = clean.substringBefore(" ").readableLinkedTaskDate()
    val time = clean.substringAfter(" ", "").readableLinkedTaskTime()
    return listOfNotNull(date, time).joinToString(" · ").ifBlank { clean }
}

private fun String.readableLinkedTaskDate(): String? {
    val parts = split("-")
    if (parts.size != 3) return null
    val month = parts[1].toIntOrNull()?.let { linkedTaskMonthNames.getOrNull(it - 1) } ?: return null
    val day = parts[2].toIntOrNull() ?: return null
    return "$month $day"
}

private fun String.readableLinkedTaskTime(): String? {
    if (":" !in this) return null
    val hour = substringBefore(":").toIntOrNull() ?: return null
    val minute = substringAfter(":").take(2).padStart(2, '0')
    val displayHour = when {
        hour == 0 -> 12
        hour > 12 -> hour - 12
        else -> hour
    }
    val suffix = if (hour >= 12) "PM" else "AM"
    return "$displayHour:$minute $suffix"
}

private val linkedTaskMonthNames = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
